import {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from 'react'
import type { User, SupabaseClient } from '@supabase/supabase-js'
import type { FCWithChildren } from '../types'
import { createClient } from '../util/supabase/component'

type SessionContextType = {
  user: User | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  setPassword: (newPw: string) => Promise<void>
}

const SessionContext = createContext<SessionContextType>({
  user: null,
  isLoading: true,
  login: async () => {},
  signOut: async () => {},
  setPassword: async () => {},
})

export const SessionProvider: FCWithChildren = ({ children }) => {
  const [supabase] = useState<SupabaseClient>(createClient)
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })

    return () => (subscription ? subscription.unsubscribe() : undefined)
  }, [supabase])

  const signOut = useCallback(async () => {
    setIsLoading(true)
    await supabase.auth.signOut()
    setIsLoading(false)
  }, [supabase])

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true)
      const {
        data: { user },
        error,
      } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw new Error(error.message)
      if (user) {
        setUser(user)
      }
    },
    [supabase],
  )

  const setPassword = useCallback(
    async (newPw: string) => {
      setIsLoading(true)
      const { error } = await supabase.auth.updateUser({ password: newPw })
      if (error) throw new Error(error.message)
      setIsLoading(false)
    },
    [supabase],
  )

  return (
    <SessionContext.Provider
      value={{ user, isLoading, signOut, login, setPassword }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export const useSession = () => useContext(SessionContext)
